import { Component, h, Element, State, Prop, Watch, Event, EventEmitter, Listen} from '@stencil/core';
import { Filter, SearchDriver } from '@elastic/search-ui';
import AppSearchAPIConnector from '@elastic/search-ui-app-search-connector';
import i18nextInstance from '../../utils/i18n';
import { urlToBreadcrumb } from '../../utils/utils';

/**
 * AdSearchbar is a searchbar component that can be used to perform search requests.
 */
@Component({
  tag: 'ad-searchbar',
  styleUrl: 'ad-searchbar.css',
  shadow: true,
})

export class SearchBar {

    searchForm: HTMLFormElement;
    searchInput: HTMLInputElement;
    submitButton: HTMLButtonElement;

    @Element() el: HTMLElement;
    @State() driverAutocomplete: SearchDriver;
    @State() appSearchConnector: AppSearchAPIConnector;

    /** ElasticSearch Endpoint URL */
    @Prop({reflect:true, mutable: true}) esEndpoint: string;
    /** ElasticSearch Enginename */
    @Prop({reflect:true, mutable: true}) esEnginename: string;
    /** Searchterm / Query which is used to perform search request */
    @Prop({reflect:true, mutable: true}) searchterm: string;
    /** Action URL for the form */
    @Prop({reflect:true}) action: string;
    /** (optional) site filter */
    @Prop({reflect:true, mutable: true}) site?: string;
    @Prop() filter: Filter[] = [];
    @State() suggestionList: any[] = [];
    @State() resultList: any[] = [];
    @State() isLoading: boolean = false;
    @State() isTranslated : boolean = false;
    
    @Listen('translationsLoaded', {target: 'window'})
    translationsLoadedHandler() {
        this.isTranslated = true;
    }

    @Event({bubbles: true, composed:true}) searchbarSubmit: EventEmitter;

    @Watch('searchterm')
    searchtermChanged(newValue: string, oldvalue: string) {
        if(newValue !== oldvalue) {
            // this.searchInput.value = newValue;
        }
    }

    // Element is connected to DOM
    connectedCallback() {
        this.appSearchConnector = new AppSearchAPIConnector({
            endpointBase: this.esEndpoint,
            engineName: this.esEnginename
        }); 

        this.driverAutocomplete = new SearchDriver({
            apiConnector: this.appSearchConnector,
			hasA11yNotifications: false,
			initialState: { searchTerm: this.searchterm},
			trackUrlState: false
        });

        this.driverAutocomplete.setAutocompleteQuery({
			results: {
			  resultsPerPage: 5, // number of results to display. Default is 5.
			  result_fields: {
				title: { snippet: { size: 100, fallback: true }},
				link: { raw: {} },
				id: { raw: {} }
			  },
			  filters: this.filter
			},
			// performs a query to suggest for values that partially match the incomplete query
			suggestions: {
			  types: {
				// Limit query to only suggest based on "title" field
				documents: {  fields: ["title","headlines"] },
			  },
			  // Limit the number of suggestions returned from the server
			  size: 3
			}
		});

        this.driverAutocomplete.subscribeToStateChanges((state) => {
			if(Object.keys(state.autocompletedSuggestions).length > 0) {
                this.suggestionList = state.autocompletedSuggestions.documents;
                this.resultList = state.autocompletedResults;
			}
		});
    }

    onSearchtermChangeHandler() {
        this.driverAutocomplete.getActions().setSearchTerm(this.searchInput.value, {
            autocompleteSuggestions:true,
            autocompleteResults:true,
            debounce: 500
        });
    }

    onKeyUpEventHandler() {
        this.isLoading = true;
        this.onSearchtermChangeHandler();
        this.isLoading = false;
    }

    onSubmitHandler(e:SubmitEvent) {
        const currentUrl = location.pathname;
        const actionUrl = this.action.split('?')[0].split('#')[0];
        if (currentUrl === actionUrl) {
			e.preventDefault();
			this.suggestionList = [];
            this.resultList = [];
            this.searchbarSubmit.emit(this.searchInput.value);
		} 
        this.searchInput.blur();
        this.submitButton.blur();        
    }

    onAutocompleteClickHandler(e:MouseEvent) {
        e.preventDefault();
        let currentTarget = e.target as HTMLElement;
        if(currentTarget.innerText) {
            this.searchInput.value = currentTarget.innerText;
            this.searchterm = currentTarget.innerText;
            if(this.searchForm.requestSubmit) {
                this.searchForm.requestSubmit();
            } else {
                this.searchForm.submit();
            }
            
        }
    }

    onResultClickHandler(e:MouseEvent) {
        e.stopPropagation();
        const id = (e.currentTarget as HTMLElement).id;
        this.driverAutocomplete.getActions().trackClickThrough(id);
    }

    render() {
        let autocomplete = (
            (this.suggestionList.length > 0 ? 
                <ul>
                    <li data-type="title"><strong>{i18nextInstance.t('suggestion', {count:this.suggestionList.length})}</strong></li>
                    {this.suggestionList.map(result => (
                        <li data-type="autocomplete"><a onClick={this.onAutocompleteClickHandler.bind(this)} href="#">{result.suggestion}</a></li>
                    ))}
                </ul>
                : ""
            )
        );

        let results = (
            (this.resultList.length > 0 ? 
            <ul>
            {this.resultList.map(result => (
                <li data-type="result">
                <a 
                    id={result.id.raw} 
                    href={result.link?.raw} 
                    innerHTML={`${result.title.snippet} <small class="breadcrumb" aria-hidden="true">${urlToBreadcrumb(result.link?.raw, ` <ad-icon icon="chevron-right"></ad-icon> ` )}</small>`}
                    onClick={this.onResultClickHandler.bind(this)} 
                />
                </li>
            ))}
            </ul>
            : "")
        );

        return (
            <form ref={form => this.searchForm = form} class="inputcontainer" action={this.action} role="searchbox" onSubmit={this.onSubmitHandler.bind(this)}>
                <div class="flex">
                    <input onKeyUp={this.onKeyUpEventHandler.bind(this)} ref={searchInput => this.searchInput = searchInput} aria-label={`${i18nextInstance.t('search')}`} autocomplete="off" placeholder={`${i18nextInstance.t('searchbarplaceholder')}`} id='search' list='searchSuggestions' name='q' type='search' value={this.searchterm} />
                    <button ref={btn => this.submitButton = btn} aria-label={`${i18nextInstance.t('submitsearch')}`} >
                        <ad-icon icon="magnifying-glass"></ad-icon>
                    </button>
                </div>
                {this.resultList.length > 0 || this.suggestionList.length > 0 ? 
                <div id="searchSuggestions">
                    {autocomplete}
                    {results}
                </div>
                : ""}
            </form>
        )
    }
}